import { useState, type FormEvent } from "react"
import { useSearchParams, Link, useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { KeyRound, ArrowLeft, CheckCircle } from "lucide-react"
import { resetPassword } from "../../api/auth"
import logo from "../../assets/logo.png"

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get("reset_password_token") ?? ""

  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError("")

    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.")
      return
    }
    if (password !== passwordConfirmation) {
      setError("Las contraseñas no coinciden.")
      return
    }

    setLoading(true)
    try {
      await resetPassword(token, password, passwordConfirmation)
      setSuccess(true)
      setTimeout(() => navigate("/login", { replace: true }), 3000)
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "No se pudo restablecer la contraseña. El enlace puede haber expirado."
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background bg-grid-pattern">
        <div className="gradient-card rounded-2xl border border-white/10 p-8 shadow-2xl max-w-md text-center space-y-4">
          <p className="text-sm text-danger">
            Enlace inválido. Asegúrate de usar el enlace completo que recibiste por email.
          </p>
          <Link to="/forgot-password" className="inline-flex items-center gap-1 text-primary hover:text-primary/80 transition text-sm">
            <ArrowLeft size={14} />
            Solicitar nuevo enlace
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background bg-grid-pattern">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="gradient-card rounded-2xl border border-white/10 p-8 shadow-2xl">
          <div className="mb-8 flex flex-col items-center gap-4">
            <img src={logo} alt="Science Fitness" className="h-16 w-auto" />
            <div className="text-center">
              <h1
                className="text-2xl font-bold tracking-widest text-primary glow-cyan-text uppercase"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Science Fitness
              </h1>
              <p className="mt-1 text-sm text-muted">Nueva Contraseña</p>
            </div>
          </div>

          {success ? (
            <div className="text-center space-y-4">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/10 border border-success/30">
                <CheckCircle size={28} className="text-success" />
              </div>
              <p className="text-sm text-muted">
                Tu contraseña ha sido restablecida exitosamente.
              </p>
              <p className="text-xs text-muted/70">Serás redirigido al inicio de sesión...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted uppercase tracking-wide">
                  Nueva Contraseña
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  className="w-full rounded-lg border border-white/10 bg-surface-elevated px-4 py-3 text-white placeholder-muted/50 focus:border-primary"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted uppercase tracking-wide">
                  Confirmar Contraseña
                </label>
                <input
                  type="password"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  placeholder="Repite la contraseña"
                  required
                  className="w-full rounded-lg border border-white/10 bg-surface-elevated px-4 py-3 text-white placeholder-muted/50 focus:border-primary"
                />
              </div>

              {error && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="rounded-lg bg-danger/10 border border-danger/30 px-3 py-2 text-sm text-danger"
                >
                  {error}
                </motion.p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary py-3 font-semibold text-black transition hover:opacity-90 glow-cyan disabled:opacity-50"
              >
                <KeyRound size={16} />
                {loading ? "Restableciendo..." : "Restablecer Contraseña"}
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-muted">
            <Link to="/login" className="inline-flex items-center gap-1 text-primary hover:text-primary/80 transition">
              <ArrowLeft size={14} />
              Volver al inicio de sesión
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  )
}
